import { Box, Text, Flex, useMantineTheme, Center, Grid } from "@mantine/core";
import { InterestContext, useAuth } from "../../context/auth";
import { formatCurrency } from "../../utils/currency";
import type { IProductPlan } from "../../interfaces/Products/IProductDetail";
import { TickCircle } from "iconsax-react";
import { useFormContext } from "react-hook-form";
import { IPostBuyPlan } from "../../interfaces/Purchase";
import { useTranslation } from "react-i18next";
interface IPlanSelectorProps {
  plan: IProductPlan;
  onSelect: () => void;
}

const PlanSelector = ({ plan, onSelect }: IPlanSelectorProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  return (
    <Flex
      onClick={onSelect}
      align={"center"}
      p={"12px"}
      gap={"8px"}
      justify={"space-between"}
      sx={(theme) => ({
        backgroundColor: plan.isSelected ? theme.colors.blue[0] : "#fff",
        border: plan.isSelected
          ? `1.5px solid ${theme.colors.blue[6]}`
          : `1px solid ${theme.colors.gray[3]}`,
        borderRadius: "8px",
        flex: 1,
        cursor: "pointer",
      })}
    >
      <Flex align={"center"} gap="8px">
        {plan.isSelected ? (
          <TickCircle
            size="28"
            color={theme.colors.primary[2]}
            variant="Bold"
          />
        ) : (
          <Center h={"28px"} w={"28px"}>
            <Box
              h={"21px"}
              w={"21px"}
              bg="#fff"
              style={{ borderRadius: "50%", border: "2px solid #6B6B6B" }}
            />
          </Center>
        )}

        <Flex direction="column" justify="center">
          <Text
            color={theme.colors.basic[5]}
            fw={600}
            fz="16px"
            style={{ whiteSpace: "nowrap" }}
          >
            {plan.name}
          </Text>

          {plan.qty_contract > 1 ? (
            <Text
              color={theme.colors.basic[4]}
              fw={500}
              fz="12px"
              style={{ whiteSpace: "nowrap" }}
            >
              {t("marketplace.comprar_serviços.purchase_step_2.limit", {
                qty_contract: plan.qty_contract,
              })}
            </Text>
          ) : (
            <Text
              color={theme.colors.basic[4]}
              fw={500}
              fz="12px"
              style={{ whiteSpace: "nowrap" }}
            >
              {t("marketplace.comprar_serviços.purchase_step_2.ilimited")}
            </Text>
          )}
        </Flex>
      </Flex>
      <Flex direction="column" justify="center">
        {plan.qty_of_installments > 0 ? (
          <Text
            color={theme.colors.basic[4]}
            fw={500}
            fz="12px"
            style={{ whiteSpace: "nowrap" }}
          >
            {plan.qty_of_installments}
            {t("marketplace.comprar_serviços.purchase_step_2.x_of")}
          </Text>
        ) : null}
        <Text color={theme.colors.basic[5]} fw={700} fz="20px">
          {plan.qty_of_installments > 0
            ? formatCurrency(plan.price / plan.qty_of_installments)
            : formatCurrency(plan.price)}
        </Text>
      </Flex>
    </Flex>
  );
};

interface IProps {
  product: InterestContext;
}

const ProductPlans = ({ product }: IProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { setInterest } = useAuth();
  const { setValue, getValues } = useFormContext<IPostBuyPlan>();

  const handlePlanSelect = (index: number) => {
    const updatedPlans = product.plan.map((plan, i) => ({
      ...plan,
      isSelected: i === index ? !plan.isSelected : false,
    }));

    setInterest((prev) =>
      prev.map((p) =>
        p.product.id === product.product.id ? { ...p, plan: updatedPlans } : p
      )
    );

    const selectedPlan = updatedPlans.find((plan) => plan.isSelected);
    const currentPlans = getValues("plans") || [];

    if (selectedPlan) {
      setValue("plans", [
        ...currentPlans.filter((p) => p.product_id !== product.product.id),
        {
          plan_id: selectedPlan.id,
          product_id: product.product.id,
          hasValue: selectedPlan.price > 0,
        },
      ]);
    } else {
      setValue(
        "plans",
        currentPlans.filter((p) => p.product_id !== product.product.id)
      );
    }
  };

  return (
    <Flex direction={"column"} gap="8px">
      <Flex gap="8px" w="100%" justify="space-between">
        <Text color={theme.colors.basic[5]} fw={600} fz="14px">
          {t("marketplace.comprar_serviços.purchase_step_2.plan")}
        </Text>
        <Text color={theme.colors.alerts[0]} fw={600} fz="16px">
          *
        </Text>
      </Flex>

      <Grid w="100%" grow gutter={"16px"}>
        {product.plan.map((plan, index) => (
          <Grid.Col span={4} key={index}>
            <PlanSelector
              plan={plan}
              onSelect={() => handlePlanSelect(index)}
            />
          </Grid.Col>
        ))}
      </Grid>
    </Flex>
  );
};

export default ProductPlans;
